import { useNavigate } from "react-router-dom";
import { ArrowRight, Percent, Coins, CalendarDays, Zap, FileText, ShieldCheck, Check } from "lucide-react";
import { useLoans } from "../../components/loans/LoansContext";
import { Breadcrumb, PageTitle, GradButton, GhostButton, IconTile } from "../../components/loans/LoansUi";
import { RateText, useSeo } from "../LoansLanding";
import { fmtLakh, summarise } from "../../lib/loanStats";

const BENEFITS = [
  { icon: Zap, label: "Quick Approval" },
  { icon: FileText, label: "Minimal Documentation" },
  { icon: CalendarDays, label: "Flexible Tenure" },
  { icon: ShieldCheck, label: "No Collateral" },
];
export const CRITERIA = ["Age: 21 to 60 years", "Stable income (Salaried or Self-employed)", "Minimum monthly income as per lender policy"];

function StatsCard() {
  const { products } = useLoans();
  const s = summarise(products);
  const cells = [
    { icon: Percent, label: "Interest Rate from", value: s.bestRate ? <RateText rate={s.bestRate} big="text-xl" /> : "—", id: "rate" },
    { icon: Coins, label: "Loan Amount Up to", value: fmtLakh(s.maxAmount), id: "amount" },
    { icon: CalendarDays, label: "Tenure Up to", value: s.maxTenure ? `${s.maxTenure} Months` : "—", id: "tenure" },
  ];
  return (
    <div data-testid="le-pl-stats" className="bg-white rounded-3xl border border-slate-100 shadow-[0_20px_60px_-25px_rgba(15,40,90,0.25)] p-6 grid grid-cols-3 divide-x divide-slate-200 le-rise" style={{ animationDelay: "150ms" }}>
      {cells.map((c) => (
        <div key={c.id} className="px-3 sm:px-5 flex flex-col items-center text-center">
          <c.icon className="w-9 h-9 text-[#10A37F]" strokeWidth={1.6} />
          <div className="mt-3 text-sm text-[#0B1B3F]">{c.label}</div>
          <div data-testid={`le-pl-stat-${c.id}`} className="mt-1 text-lg sm:text-xl font-extrabold">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

export function CriteriaList({ items = CRITERIA }) {
  return (
    <ul className="space-y-3">
      {items.map((c) => <li key={c} className="flex items-start gap-3"><Check className="w-5 h-5 text-[#10A37F] mt-0.5 shrink-0" strokeWidth={3} /> <span>{c}</span></li>)}
    </ul>
  );
}

export default function LoansPersonalLoan() {
  useSeo("Personal Loan · loanexpress", "Quick personal loans with flexible amounts, easy EMIs and competitive interest rates from trusted lenders.");
  const { products, apply } = useLoans();
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 md:px-10 pt-10 pb-20">
      <Breadcrumb items={[{ label: "Loan Products" }, { label: "Personal Loan" }]} />
      <div className="mt-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <PageTitle plain="Personal" accent="Loan" sub="Get quick funds for your personal needs with flexible amounts, easy EMIs and competitive interest rates." />
          <div className="mt-8 flex flex-wrap gap-4">
            <GradButton data-testid="le-pl-apply" onClick={() => apply(products[0])} disabled={!products.length} className="h-14 px-8 rounded-full">Apply for Personal Loan <ArrowRight className="w-4 h-4" /></GradButton>
            <GhostButton data-testid="le-pl-eligibility" onClick={() => nav("/check-eligibility")} className="h-14 px-8 rounded-full">Check Eligibility</GhostButton>
          </div>
        </div>
        <StatsCard />
      </div>
      <h2 className="mt-20 text-3xl font-extrabold tracking-tight">Why Choose a Personal Loan?</h2>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-8 md:divide-x divide-slate-200">
        {BENEFITS.map((b) => (
          <div key={b.label} data-testid={`le-pl-benefit-${b.label.split(" ")[0].toLowerCase()}`} className="flex flex-col items-center text-center gap-4 group">
            <IconTile icon={b.icon} className="group-hover:-translate-y-1 transition-transform" />
            <div className="text-lg font-semibold max-w-[9rem]">{b.label}</div>
          </div>
        ))}
      </div>
      <div data-testid="le-pl-criteria" className="mt-16 rounded-3xl bg-gradient-to-r from-[#EAF6FB] to-[#ECFAF4] border border-slate-100 p-8 md:p-12 grid md:grid-cols-[1fr_1.4fr] gap-8 items-center">
        <h2 className="text-3xl font-extrabold tracking-tight md:border-r border-slate-300 md:pr-8">Eligibility Criteria</h2>
        <div className="text-lg"><CriteriaList /></div>
      </div>
    </section>
  );
}
